import React, { useState, useEffect, useCallback } from 'react';
import axios, { API } from '../lib/api';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Sparkles, Flag, AlertTriangle, Shield, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const getEmptyMessage = (filter) => {
  if (filter === 'flagged') return 'No flagged responses. Your AI coach is doing well!';
  if (filter === 'week') return 'No AI responses generated this week yet.';
  return "No AI responses yet. They'll appear once beta testers request coaching feedback.";
};

const AIMonitorTab = () => {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ total: 0, flagged: 0, this_week: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [flagDialogOpen, setFlagDialogOpen] = useState(false);
  const [flagTarget, setFlagTarget] = useState(null);
  const [flagReason, setFlagReason] = useState('');
  const [submittingFlag, setSubmittingFlag] = useState(false);

  const fetchData = useCallback(async (activeFilter) => {
    setLoading(true);
    try {
      const [listResp, statsResp] = await Promise.all([
        axios.get(`${API}/ai-responses/admin`, { params: { filter: activeFilter } }),
        axios.get(`${API}/ai-responses/admin/stats`),
      ]);
      setItems(listResp.data || []);
      setStats(statsResp.data || { total: 0, flagged: 0, this_week: 0 });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load AI responses');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(filter);
  }, [fetchData, filter]);

  const submitFlag = async () => {
    if (!flagTarget) return;
    setSubmittingFlag(true);
    try {
      await axios.post(`${API}/ai-responses/admin/${flagTarget.task_id}/flag`, { reason: flagReason.trim() || null });
      toast.success('Flagged for review');
      setFlagDialogOpen(false);
      setFlagTarget(null);
      setFlagReason('');
      fetchData(filter);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to flag');
    } finally {
      setSubmittingFlag(false);
    }
  };

  const handleUnflag = async (item) => {
    try {
      await axios.post(`${API}/ai-responses/admin/${item.task_id}/unflag`);
      toast.success('Flag cleared');
      fetchData(filter);
    } catch {
      toast.error('Failed to clear flag');
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm("Delete this AI response? The user won't see it anymore (they can regenerate).")) return;
    try {
      await axios.delete(`${API}/ai-responses/admin/${item.task_id}`);
      toast.success('AI response deleted');
      fetchData(filter);
    } catch {
      toast.error('Failed to delete');
    }
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-justb-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading AI responses...</p>
        </div>
      );
    }

    if (items.length === 0) {
      return (
        <Card>
          <CardContent className="py-12 text-center">
            <Sparkles className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-600">{getEmptyMessage(filter)}</p>
          </CardContent>
        </Card>
      );
    }

    return (
      <div className="space-y-3">
        {items.map((item) => {
          const dateStr = item.generated_at ? new Date(item.generated_at).toLocaleString() : '\u2014';
          return (
            <Card key={item.task_id} className={item.flagged ? 'border-red-300 bg-red-50/30' : ''}>
              <CardContent className="pt-5">
                <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      {item.flagged && (
                        <Badge variant="destructive" className="flex items-center gap-1">
                          <AlertTriangle className="h-3 w-3" />
                          Flagged
                        </Badge>
                      )}
                      <span className="text-sm font-medium text-gray-900 truncate">{item.goal_title}</span>
                    </div>
                    <p className="text-xs text-gray-600">{item.task_description}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      <span className="font-medium">{item.username}</span> &middot; {dateStr}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {item.flagged ? (
                      <Button variant="ghost" size="sm" onClick={() => handleUnflag(item)} className="text-gray-600 hover:text-gray-900" title="Clear flag">
                        Unflag
                      </Button>
                    ) : (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => { setFlagTarget(item); setFlagReason(''); setFlagDialogOpen(true); }}
                        className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                        title="Flag for review"
                      >
                        <Flag className="h-4 w-4" />
                      </Button>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(item)} className="text-red-600 hover:text-red-700 hover:bg-red-50" title="Delete AI response">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                {item.flagged && item.flag_reason && (
                  <div className="mb-3 text-sm bg-white border border-red-200 rounded px-3 py-2">
                    <span className="font-semibold text-red-700">Flag reason: </span>
                    <span className="text-gray-800">{item.flag_reason}</span>
                  </div>
                )}

                <div className="rounded-lg border border-justb-primary/20 bg-gradient-to-br from-justb-primary/5 via-white to-amber-50/40 p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles className="h-4 w-4 text-justb-primary" />
                    <span className="text-xs font-semibold text-justb-primary uppercase tracking-wide">AI Coach response</span>
                  </div>
                  <p className="text-sm text-gray-800 whitespace-pre-wrap break-words leading-relaxed">{item.ai_feedback}</p>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
        <Shield className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-amber-900">
          <p className="font-medium">For admin use only — handle user data responsibly.</p>
          <p className="text-amber-800">
            User reflections are <strong>not shown</strong> here to protect privacy. Only the AI coach's responses are visible.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Card><CardContent className="pt-5"><p className="text-xs text-gray-500 uppercase tracking-wide">Total AI responses</p><p className="text-2xl font-bold text-gray-900 mt-1">{stats.total}</p></CardContent></Card>
        <Card><CardContent className="pt-5"><p className="text-xs text-gray-500 uppercase tracking-wide flex items-center gap-1"><Flag className="h-3 w-3 text-red-500" /> Flagged</p><p className="text-2xl font-bold text-red-600 mt-1">{stats.flagged}</p></CardContent></Card>
        <Card><CardContent className="pt-5"><p className="text-xs text-gray-500 uppercase tracking-wide">Generated this week</p><p className="text-2xl font-bold text-justb-primary mt-1">{stats.this_week}</p></CardContent></Card>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm text-gray-600 mr-1">Show:</span>
        <Button variant={filter === 'all' ? 'default' : 'outline'} size="sm" onClick={() => setFilter('all')} className={filter === 'all' ? 'bg-justb-primary hover:bg-justb-primary/90' : ''}>All ({stats.total})</Button>
        <Button variant={filter === 'flagged' ? 'default' : 'outline'} size="sm" onClick={() => setFilter('flagged')} className={filter === 'flagged' ? 'bg-red-600 hover:bg-red-700' : ''}>
          <Flag className="h-3 w-3 mr-1" />Flagged ({stats.flagged})
        </Button>
        <Button variant={filter === 'week' ? 'default' : 'outline'} size="sm" onClick={() => setFilter('week')} className={filter === 'week' ? 'bg-justb-primary hover:bg-justb-primary/90' : ''}>This week ({stats.this_week})</Button>
      </div>

      {renderContent()}

      <Dialog open={flagDialogOpen} onOpenChange={setFlagDialogOpen}>
        <DialogContent className="sm:max-w-[460px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Flag className="h-5 w-5 text-amber-600" />Flag this AI response</DialogTitle>
            <p className="text-sm text-gray-500 pt-1">Mark this response for review. The user won't see that it's flagged.</p>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="flag-reason" className="mb-2 block">Reason <span className="text-gray-400 font-normal">(optional)</span></Label>
              <Textarea id="flag-reason" value={flagReason} onChange={(e) => setFlagReason(e.target.value)} placeholder="e.g. Too harsh, gave medical advice, off-topic…" rows={3} maxLength={500} />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setFlagDialogOpen(false)} disabled={submittingFlag}>Cancel</Button>
              <Button onClick={submitFlag} disabled={submittingFlag} className="bg-amber-600 hover:bg-amber-700">{submittingFlag ? 'Flagging…' : 'Flag for review'}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AIMonitorTab;
